import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MatterCollaborationEntrySource } from '@/analytics/enums/matter-collaboration.enums'
import {
  getCollaborationOrganizationId,
  trackMatterInviteSendFailed,
  trackMatterInviteSentForRecipients,
} from '@/analytics/utils/matter-collaboration-analytics'
import { getMatterJurisdictionAnalyticsProps, resolveMatterJurisdictionEntrySource } from '@/analytics/utils/matter-jurisdiction-analytics'
import { CreateOrUpdateMatterForm, CreateOrUpdateMatterSchema, MatterRead } from '@/matter/store/schemas'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { kPaxtonName } from '@/constants/constants-strings'
import { zodResolver } from '@hookform/resolvers/zod'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { selectMatterById } from '../../store/selectors'
import InputFieldFloatingLabel from '@/components/input-field-floating-label/InputFieldFloatingLabel'
import FloatLabelFormTextarea from './FloatLabelFormTextArea'
import {
  kBEMatterAlreadyExistsDetail,
  kMatterDialogGenericErrorMessage,
  kMatterNameExistsErrorMessage,
  kMaxMatterContextLength,
  kMaxMatterHardLimit,
} from '@/matter/constants'
import MatterCharacterCounter from './MatterCharacterCounter'
import { ApiError } from '@/util/errors/api-error'
import { putUpdateMatter } from '@/matter/fetch/put-update-matter'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { postCreateMatter } from '@/matter/fetch/post-create-matter'
import { postAddMatterMembers } from '@/matter/fetch/post-add-matter-members'
import { AddMemberOutcome, type MatterAssignableRole } from '@/matter/components/manage-members/schemas'
import { setMobileSidebarOpen } from '@/store/slices/ui-state.slice'
import { matterPaths } from '@/routing/paths'
import trim from 'lodash/trim'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { replaceMatterSegment } from '@/matter/utils/path'
import JurisdictionField from '@/jurisdiction-mappings/components/JurisdictionField'
import { JurisdictionKind, useJurisdictionFieldState } from '@/jurisdiction-mappings/jurisdiction-field-state'
import { useSettings } from '@/settings/hooks/useSettings'
import { selectDefaultJurisdiction } from '@/chat-common/store/chat-ux.selectors'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { Typeahead } from '@/components/typeahead/Typeahead'
import { useIsAccountCollaborationCapable } from '@/collaboration/hooks/useIsAccountCollaborationCapable'
import { useOrgDirectoryUsers } from '@/matter/hooks/useOrgDirectoryUsers'
import { InvitationRoleMenu } from '@/matter/components/manage-members/InvitationRoleMenu'
import { InvitationRoleHint } from '@/matter/components/manage-members/InvitationRoleHint'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { AuthContext } from '@/context/auth-context'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { useCreateMatterFolderSync } from '@/matter/hooks/useCreateMatterFolderSync'
import { ExternalFolderField } from '@/user_files/external-storage/components/ExternalFolderField'

/**
 * Create or Edit Matter Dialog
 *
 * Dialog to create or edit a matter's details.
 */
export default function CreateOrEditMatterDialog() {
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const dispatch = useAppDispatch()
  const brandName = kPaxtonName
  const location = useLocation()
  const isUserFilesRoute = useMatch('/dashboard/user-files/*')
  const formRef = useRef<HTMLFormElement>(null)

  // Local state
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([])
  const [invitationRole, setInvitationRole] = useState<MatterAssignableRole>('collaborator')
  const { enabled: editorEnabled, loading: editorLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.COLLAB_EDITOR_ROLE)
  const canInviteEditors = editorEnabled && !editorLoading
  const effectiveInvitationRole = canInviteEditors ? invitationRole : 'collaborator'

  useEffect(() => {
    if (!canInviteEditors) setInvitationRole('collaborator')
  }, [canInviteEditors])

  // Redux state
  const dialogState = useAppSelector((state: RootState) => state.mattersState.dialog)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: dialogState.matterId }))
  const defaultJurisdiction = useAppSelector((state: RootState) => selectDefaultJurisdiction(state))
  const isMobileSidebarOpen = useAppSelector((state: RootState) => state.uiState.isMobileSidebarOpen)
  const { getSettings } = useSettings()

  const dialogType = dialogState.dialogType
  const isEditType = dialogType === MatterDialogType.Edit
  const isDialogOpen = dialogType === MatterDialogType.Create || dialogType === MatterDialogType.Edit
  const driveSyncEnabled = dialogType === MatterDialogType.Create && kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const { connectedAccount, accountsRequest, selectedFolder, setSelection, connectFolder } = useCreateMatterFolderSync(driveSyncEnabled)

  // Add Team Members (create mode only, org accounts only)
  const { user, userAccountData } = useContext(AuthContext)
  const collaborationOrganizationId = getCollaborationOrganizationId(userAccountData)
  const isCollaborationCapable = useIsAccountCollaborationCapable()
  const { orgUsers, isLoading: isOrgUsersLoading } = useOrgDirectoryUsers(isCollaborationCapable && !isEditType && isDialogOpen)
  const memberTypeaheadItems = orgUsers.filter((u) => u.userId !== user?.uid).map((u) => ({ id: u.userId, label: u.displayName, description: u.email }))
  const showAddMembers = !isEditType && isCollaborationCapable && (isOrgUsersLoading || memberTypeaheadItems.length > 0)

  // Initial form values based on whether we're editing a matter or creating a new one, only recalculated when the dialogType or matter changes
  const initialFormValues = useMemo<CreateOrUpdateMatterForm>(() => {
    return isEditType
      ? // Edit uses the existing matter's data
        {
          name: matter?.name ?? '',
          client: matter?.client ?? '',
          description: matter?.description ?? '',
        }
      : // Create uses empty values
        {
          name: '',
          client: '',
          description: '',
        }
  }, [isEditType, matter])

  const initialJurisdiction = isEditType ? matter?.default_jurisdiction ?? null : defaultJurisdiction
  const jurisdictionField = useJurisdictionFieldState(initialJurisdiction, isEditType ? JurisdictionKind.UNDECIDED : JurisdictionKind.STATE)

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isValid, isDirty },
    reset,
    setFocus,
    setError: setFieldError,
    watch,
  } = useForm<CreateOrUpdateMatterForm>({
    resolver: zodResolver(CreateOrUpdateMatterSchema),
    mode: 'onChange',
    defaultValues: initialFormValues,
  })

  // Watch form values for character limit calculation
  const currentValues = watch()

  // Calculate total character count (raw user text only, excluding labels)
  const totalCharCount = useMemo(() => {
    const name = currentValues.name || ''
    const client = currentValues.client || ''
    const description = currentValues.description || ''

    return name.length + client.length + description.length
  }, [currentValues.name, currentValues.client, currentValues.description])

  // Allow submission if the form is valid, there are actual changes, and under hard limit
  const canSubmit = isValid && (isDirty || jurisdictionField.isDirty) && jurisdictionField.isValid && totalCharCount <= kMaxMatterHardLimit

  // Unique key per dialog session
  const formKey = `${dialogType}-${dialogState.matterId ?? 'new'}`

  /*
   * Reset the form when switching between create/edit modes or editing a different matter.
   * Tracked via `formKey`, which changes when dialog type or matter ID changes.
   */
  useEffect(() => {
    reset(initialFormValues)
    jurisdictionField.reset()
    setSubmitError(null)
    setIsSubmitting(false)
    setSelectedMemberIds([])
    setInvitationRole('collaborator')
    setSelection(null)

    // Autofocus the name field on Create. The shared Modal always focuses its own container on
    // open (see ModalContainer's onOpenAutoFocus), so we set focus ourselves after a short delay
    // to account for Radix dialog portal mounting and animations.
    if (isDialogOpen && !isEditType) {
      const timeoutId = setTimeout(() => setFocus('name'), 100)
      return () => clearTimeout(timeoutId)
    }
    // initialFormValues is derived from the same dependencies as formKey, so it's safe to exclude
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey, reset])

  useEffect(() => {
    if (!isEditType && dialogType === MatterDialogType.Create) {
      getSettings()
    }
    // getSettings is omitted so this runs once per create-dialog open, not once per settings hook render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogType, isEditType])

  // Handles success actions after a matter is created or updated
  const onSuccess = (
    savedMatter: MatterRead,
    { memberAddFailed, folderConnectFailed, folderSelected }: { memberAddFailed: boolean; folderConnectFailed: boolean; folderSelected: boolean }
  ) => {
    // Update the store with the saved matter
    dispatch(MattersActions.upsertMatter(savedMatter))

    // If it's a create, set the new matter as the active matter (unless the caller opted out)
    if (!isEditType && dialogState.switchToMatterOnCreate) {
      // IMPORTANT: Navigate FIRST, then update Redux state.
      // This ensures the URL is correct when route handler effects run,
      // preventing race conditions and duplicate history entries.
      if (isUserFilesRoute) {
        const path = replaceMatterSegment(location.pathname, savedMatter.id)
        if (path !== location.pathname) {
          navigate(path)
        }
      }

      // Then update Redux state
      dispatch(MattersActions.setActiveMatterId({ matterId: savedMatter.id }))
      if (isMobileSidebarOpen) dispatch(setMobileSidebarOpen(false)) // When creating a new matter from the sidebar on small screens
    }

    if (!isEditType && dialogState.navigateToMatterOnCreate) {
      navigate(matterPaths.overview(savedMatter.id))
    }

    // Show a success toast
    const toastMsg = isEditType
      ? `Updated matter: ${savedMatter.name}`
      : dialogState.switchToMatterOnCreate
      ? `Created and switched to matter: ${savedMatter.name}`
      : `Created matter: ${savedMatter.name}`
    const setupFailed = memberAddFailed || folderConnectFailed
    const warningMessage = folderConnectFailed
      ? memberAddFailed
        ? 'Matter created, but members could not be added and folder sync could not be confirmed. Open the matter to finish setup.'
        : 'Matter created, but folder sync could not be confirmed. Check the connection in Matter Settings and retry if needed.'
      : 'Matter created, but members could not be added. Open the matter to try again.'
    dispatch(
      openGlobalToast({
        type: setupFailed ? GlobalToastType.WARNING : GlobalToastType.SUCCESS,
        message: setupFailed ? warningMessage : `${toastMsg}${folderSelected ? '. Folder sync started.' : ''}`,
        durationMs: kToastDurationMediumMs,
        truncateAtLine: 3,
        ...(setupFailed && {
          withLink: {
            label: folderConnectFailed ? 'Matter Settings' : 'Open matter',
            href: folderConnectFailed ? matterPaths.settings(savedMatter.id) : matterPaths.overview(savedMatter.id),
          },
        }),
      })
    )

    setIsSubmitting(false)
    dispatch(MattersActions.closeDialog())
  }

  /**
   * Handles form submission for creating or editing a matter.
   *
   * - Sends form data to the appropriate API endpoint
   * - On success: tracks analytics, updates Redux state, shows a toast, and closes the dialog
   * - On error: sets appropriate field or global error messages for user feedback
   */
  const onSubmit = async (formData: CreateOrUpdateMatterForm) => {
    if (isSubmitting) return
    setSubmitError(null)

    const jurisdictionValidationError = jurisdictionField.validate()
    if (jurisdictionValidationError) {
      return
    }

    const matterPayload: CreateOrUpdateMatterForm = {
      ...formData,
      default_jurisdiction: jurisdictionField.value,
    }

    setIsSubmitting(true)

    let savedMatter: MatterRead
    let memberAddFailed = false
    let folderConnectFailed = false

    try {
      if (isEditType) {
        // Edit Matter Form Submission
        if (!dialogState.matterId) {
          throw new Error('Editing a matter requires a matter id, but none was provided.')
        }
        savedMatter = await putUpdateMatter(dialogState.matterId, matterPayload)

        const savedJurisdiction = savedMatter.default_jurisdiction ?? null

        trackEvent(AnalyticsEvent.MatterUpdated, {
          matterNameChanged: (savedMatter.name ?? '') !== initialFormValues.name,
          matterClientChanged: (savedMatter.client ?? '') !== initialFormValues.client,
          matterDescriptionChanged: (savedMatter.description ?? '') !== initialFormValues.description,
          matterJurisdictionEntrySource: resolveMatterJurisdictionEntrySource(dialogState.source),
          matterJurisdictionChanged: savedJurisdiction !== initialJurisdiction,
          matterJurisdictionAdopted: initialJurisdiction == null && savedJurisdiction != null,
          ...getMatterJurisdictionAnalyticsProps(savedJurisdiction),
        })
      } else {
        // Create Matter Form Submission
        savedMatter = await postCreateMatter(matterPayload)

        // The picker only stages a choice. A real matter is required before connecting it.
        folderConnectFailed = !(await connectFolder(savedMatter.id))

        trackEvent(AnalyticsEvent.MatterCreated, getMatterJurisdictionAnalyticsProps(savedMatter.default_jurisdiction))

        // Add selected members immediately after creation (best-effort — failure doesn't block the matter).
        // Analytics runs only after product outcome is decided; helpers also swallow delivery errors.
        if (selectedMemberIds.length > 0) {
          let invitedRecipientUserIds: string[] = []
          let inviteRequestError: unknown = null
          try {
            const response = await postAddMatterMembers(savedMatter.id, selectedMemberIds, effectiveInvitationRole)
            invitedRecipientUserIds = response.results.filter((result) => result.outcome === AddMemberOutcome.INVITED).map((result) => result.user_id)
            if (response.results.some((r) => r.outcome === AddMemberOutcome.NOT_FOUND)) {
              memberAddFailed = true
            }
          } catch (error) {
            memberAddFailed = true
            inviteRequestError = error
          }

          if (inviteRequestError != null) {
            trackMatterInviteSendFailed({
              track: trackEvent,
              matterId: savedMatter.id,
              organizationId: collaborationOrganizationId,
              selectedRecipientCount: selectedMemberIds.length,
              matterInviteEntrySource: MatterCollaborationEntrySource.MatterCreation,
              error: inviteRequestError,
            })
          } else {
            trackMatterInviteSentForRecipients({
              track: trackEvent,
              matterId: savedMatter.id,
              organizationId: collaborationOrganizationId,
              selectedRecipientCount: selectedMemberIds.length,
              invitedRecipientUserIds,
              matterInviteEntrySource: MatterCollaborationEntrySource.MatterCreation,
            })
          }
        }
      }

      // Close with one outcome message, including any setup that needs another attempt.
      setTimeout(() => {
        onSuccess(savedMatter, { memberAddFailed, folderConnectFailed, folderSelected: selectedFolder != null })
      }, 600)
    } catch (e) {
      // If there's a 409 Conflict (e.g. duplicate name), display the error on the "name" field
      if (e instanceof ApiError && e.status === 409) {
        let errorMessage = kMatterDialogGenericErrorMessage
        if (e.detail === kBEMatterAlreadyExistsDetail) errorMessage = kMatterNameExistsErrorMessage

        setFieldError('name', {
          type: 'server',
          message: errorMessage,
        })
        setFocus('name')
      } else {
        setSubmitError(kMatterDialogGenericErrorMessage)
      }
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      open={isDialogOpen}
      onOpenChange={() => {
        if (!isSubmitting) dispatch(MattersActions.closeDialog())
      }}
    >
      <ModalContent
        key={formKey}
        title="Matter Details"
        subtitle={`Add case details to help ${brandName} understand and assist with this matter.`}
        width="lg"
        height="max-xl"
        bodyClassName="[scrollbar-color:theme(colors.brand-neutral.400)_transparent]"
        preventDismiss
        footer={
          <ModalFooterWithActions
            tertiaryText={
              <span>
                <span className="text-red-700">*</span> Required fields
              </span>
            }
            secondaryAction={{ label: 'Cancel', disabled: isSubmitting, onClick: () => dispatch(MattersActions.closeDialog()) }}
            primaryAction={{
              label: 'Save',
              onClick: () => formRef.current?.requestSubmit(),
              loading: isSubmitting,
              disabled: !canSubmit || isSubmitting,
            }}
            inlineMessage={submitError ? { variant: 'error', children: submitError } : undefined}
          />
        }
      >
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <fieldset className="min-w-0 space-y-4" disabled={isSubmitting}>
            {/* Matter Name */}
            <InputFieldFloatingLabel
              id="matter-name"
              label="Matter Name"
              required
              error={formErrors.name?.message}
              registration={register('name', {
                setValueAs: trim,
              })}
            />

            <JurisdictionField
              {...jurisdictionField.fieldState}
              idPrefix="matter-jurisdiction"
              error={jurisdictionField.error ?? undefined}
              modal
              onChange={jurisdictionField.onChange}
            />

            {driveSyncEnabled && connectedAccount && (
              <ExternalFolderField
                key={connectedAccount.id}
                account={connectedAccount}
                value={selectedFolder}
                onChange={setSelection}
                disabled={isSubmitting}
              />
            )}
            {driveSyncEnabled && !connectedAccount && !accountsRequest.hasLoaded && accountsRequest.status === 'loading' && (
              <p role="status" className="text-sm text-brand-neutral-600">
                Checking connected file drives…
              </p>
            )}

            {/* Add Team Members — create mode only, org users only */}
            {showAddMembers && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1 pr-0.5">
                  <p className="min-w-0 flex-1 text-base font-medium tracking-[-0.32px] text-brand-neutral-950">Add Team Members</p>
                  {canInviteEditors && (
                    <InvitationRoleMenu role={effectiveInvitationRole} onChange={setInvitationRole} disabled={isSubmitting || isOrgUsersLoading} />
                  )}
                </div>
                <Typeahead
                  items={memberTypeaheadItems}
                  selectedIds={selectedMemberIds}
                  onChange={setSelectedMemberIds}
                  disabled={isOrgUsersLoading}
                  placeholder="Search your organization"
                  ariaLabel="Search your organization"
                  emptyStateTitle="Everyone in your organization is already on this matter"
                  noResultsTitle='No results for "{searchTerm}" in your organization'
                  noResultsDescription="Only members of your organization can be added to a matter."
                  selectAllLabel="Select all {count} members"
                  modal
                />
                <InvitationRoleHint role={effectiveInvitationRole} />
              </div>
            )}

            {/* Additional Details */}
            <div className="flex flex-col gap-3">
              <p className="text-base font-medium tracking-[-0.32px] text-brand-neutral-950">Additional Details</p>
              <div className="flex flex-col gap-4">
                <InputFieldFloatingLabel
                  id="matter-client"
                  label="Client"
                  error={formErrors.client?.message}
                  registration={register('client', {
                    setValueAs: trim,
                  })}
                />
                <FloatLabelFormTextarea
                  id="matter-description"
                  label="Matter Context"
                  error={formErrors.description?.message}
                  registration={register('description', {
                    setValueAs: trim,
                  })}
                />
              </div>
            </div>
          </fieldset>

          {/* Character counter */}
          <MatterCharacterCounter totalCharCount={totalCharCount} softLimit={kMaxMatterContextLength} hardLimit={kMaxMatterHardLimit} />
        </form>
      </ModalContent>
    </Modal>
  )
}
